import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { isInMigrationWindow, MIGRATION_MESSAGE } from '../utils/migrationUtils';


                /**
                * @module Components/Autologin
                * @description Composant permettant de rediriger l'utilisateur vers une URL avec un token d'authentification
                * - Le composant prend en paramètre l'URL de redirection.
                * - Il utilise le hook useSelector de 'react-redux' pour accéder à l'ID de l'utilisateur dans le store Redux.
                * - Il utilise le hook useState de React pour gérer l'état de l'erreur.
                * - Il utilise la fonction fetch pour appeler l'API et récupérer un token d'authentification.
                * - Lorsque l'utilisateur clique sur le bouton 'Je découvre !', le composant appelle l'API pour obtenir un token,
                * puis redirige l'utilisateur vers l'URL spécifiée avec le token.
                * - Si une erreur se produit lors de l'appel à l'API, elle est affichée à l'utilisateur.
                * - Le composant affiche un bouton 'Je découvre !' pour permettre à l'utilisateur de déclencher la redirection.
                */
const AutoRedirect = ({ redirectUrl }) => {
    const apiPrefix = process.env.REACT_APP_API_PREFIX;
    
    const [error, setError] = useState(null);
    const [showMigrationModal, setShowMigrationModal] = useState(false);
    const [pendingRedirect, setPendingRedirect] = useState(null);
    const userId = useSelector(state => state.user.user.user.id);
    const sessionToken = useSelector(state => state.user.token);

    const fetchToken = async () => {
        try {
            const response = await fetch(`${apiPrefix}autologin/token/${userId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${sessionToken}`,
                }
            });
            if (!response.ok) {
                throw new Error('Failed to fetch token');
            }
            const data = await response.json();
            return data.token;
        } catch (error) {
            setError(error.message);
        }
    };

    const doRedirect = (token) => {
        if (redirectUrl.includes('app.reducce.fr')) {
            window.location.href = `${redirectUrl}${token}?organisation=CA-CEST-POUR-MOI`;
        } else {
            const finalUrl = `${redirectUrl}${token}`;
            window.open(finalUrl, '_blank');
        }
    };

    const handleRedirect = async () => {
        try {
            const token = await fetchToken();
            if (token) {
                if (isInMigrationWindow()) {
                    setPendingRedirect({ token });
                    setShowMigrationModal(true);
                } else {
                    doRedirect(token);
                }
            } else {
                throw new Error('Failed to fetch token');
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleConfirmMigrationRedirect = () => {
        if (pendingRedirect?.token) {
            doRedirect(pendingRedirect.token);
            setPendingRedirect(null);
            setShowMigrationModal(false);
        }
    };

    const handleCancelMigrationRedirect = () => {
        setPendingRedirect(null);
        setShowMigrationModal(false);
    };

    return (
        <div className="text-center">
            {error && (
                <div className="alert alert-error mb-4">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    Erreur: {error}
                </div>
            )}
            <button 
                className="btn-primary btn-ripple text-lg px-8 py-4"
                onClick={handleRedirect}
            >
                <i className="fas fa-rocket mr-2"></i>
                Je découvre !
            </button>

            {/* Modale d'avertissement migration */}
            {showMigrationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 shadow-xl max-w-md w-full text-center relative mx-4">
                        <button
                            onClick={handleCancelMigrationRedirect}
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                        >
                            &times;
                        </button>
                        <div className="mb-4">
                            <i className="fas fa-info-circle text-4xl text-amber-500 mb-3"></i>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Information</h3>
                            <p className="text-gray-600">{MIGRATION_MESSAGE}</p>
                        </div>
                        <button
                            onClick={handleConfirmMigrationRedirect}
                            className="btn-primary"
                        >
                            Continuer
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutoRedirect;
